import { ActionReference } from "@shared/types";
import styles from "../../styles";

/**
 * Component for rendering action buttons
 */
export class ActionButtonsComponent extends HTMLElement {
    private _actions: ActionReference[] = [];
    private _selectedAction: ActionReference | null = null;
    private _onActionClick: ((action: ActionReference) => void) | null = null;

    /**
     * Set the available actions
     */
    public set actions(actions: ActionReference[]) {
        this._actions = actions;
        this.render();
    }

    /**
     * Set the selected action
     */
    public set selectedAction(action: ActionReference | null) {
        this._selectedAction = action;
        this.render();
    }

    /**
     * Set the action click handler
     */
    public set onActionClick(handler: (action: ActionReference) => void) {
        this._onActionClick = handler;
    }

    /**
     * Called when the element is connected to the DOM
     */
    public connectedCallback(): void {
        this.render();
        this.setupEventListeners();
    }

    /**
     * Called when the element is disconnected from the DOM
     */
    public disconnectedCallback(): void {
        this.removeEventListeners();
    }

    /**
     * Render the component
     */
    private render(): void {
        const content: string = `
            <style>${styles.actions}</style>
            <div class="action-buttons">
                ${this.renderActionButtons()}
            </div>
        `;
        this.innerHTML = content;
    }

    /**
     * Render the action buttons
     */
    private renderActionButtons(): string {
        if (this._actions.length === 0) {
            return "";
        }

        let buttonsHtml: string = "";

        for (const action of this._actions) {
            if (action.isTravelAction) {
                continue;
            }
            const isSelected: boolean = this._selectedAction === action;
            const selectedClass: string = isSelected ? "selected" : "";

            buttonsHtml += `
                <button class="action-button ${selectedClass}" data-pop-up-alias="${action.popUpAlias}" 
                        data-alias="${action.alias}">
                    ${action.name}
                </button>
            `;
        }

        return buttonsHtml;
    }

    /**
     * Setup event listeners for action buttonsnow
     */
    private setupEventListeners(): void {
        this.addEventListener("click", this.handleActionClick.bind(this));
    }

    /**
     * Remove event listeners
     */
    private removeEventListeners(): void {
        this.removeEventListener("click", this.handleActionClick.bind(this));
    }

    /**
     * Handle action button click
     */
    private handleActionClick(event: MouseEvent): void {
        const target: HTMLElement = event.target as HTMLElement;
        const buttonElement: HTMLElement | null = target.closest(".action-button:not(.disabled)");

        if (!buttonElement) {
            return;
        }

        const popUpAlias: string | null = buttonElement.getAttribute("data-pop-up-alias");
        if (popUpAlias) {
            this.showPopUp(popUpAlias);
        }

        const actionAlias: string | null = buttonElement.getAttribute("data-alias");
        if (!actionAlias) {
            return;
        }

        const action: ActionReference | undefined = this._actions.find(a => a.alias === actionAlias);
        if (!action || !this._onActionClick) {
            return;
        }

        // Update the selected action
        this._selectedAction = action;
        this.render();

        // Call the callback with the selected action
        this._onActionClick(action);
    }

    /**
     * Show the pop up
     */
    private showPopUp(popUpAlias: string | null): void {
        if (popUpAlias === "null" || popUpAlias === null) {
            return;
        }

        const popUp: HTMLElement = document.createElement("mini-game");
        popUp.setAttribute("data-pop-up-alias", popUpAlias);
        document.body.appendChild(popUp);
    }
}
